import axios from 'axios';

// Configuration API universelle pour tous environnements
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({ baseURL: API, timeout: 10000, headers: { 'Content-Type': 'application/json' } });
const adminApiClient = axios.create({ baseURL: `${API}/admin`, timeout: 20000 });
adminApiClient.interceptors.request.use((config) => {
  const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
  if (token) { config.headers = config.headers || {}; config.headers['Authorization'] = `Basic ${token}`; }
  return config;
});

export const setAdminCredentials = (u,p) => { if (typeof window==='undefined') return; localStorage.setItem('adminAuth', btoa(`${u}:${p}`)); };
export const clearAdminCredentials = () => { if (typeof window==='undefined') return; localStorage.removeItem('adminAuth'); };

export const productsAPI = { getAll: async()=> (await apiClient.get('/products')).data, getById: async(id)=> (await apiClient.get(`/products/${id}`)).data, getByCategory: async(c)=> (await apiClient.get(`/products/category/${c}`)).data };
export const publicUploadAPI = { uploadImage: async(file)=> (await apiClient.post('/upload_public', (d=>{const f=new FormData(); f.append('file', file); return f;})())).data };
export const commentsAPI = { list: async(pid)=> (await apiClient.get(`/comments/${pid}`)).data, create: async(payload)=> (await apiClient.post('/comments', payload)).data };
export const quotesAPI = { 
  create: async(q)=> (await apiClient.post('/quotes', { 
    name: q.name, 
    email: q.email, 
    phone: q.phone, 
    projectType: q.projectType, 
    message: q.message,
    appliancesData: q.appliancesData 
  })).data, 
  getAll: async()=> (await apiClient.get('/quotes')).data, 
  getById: async(id)=> (await apiClient.get(`/quotes/${id}`)).data, 
  updateStatus: async(id,status)=> (await apiClient.put(`/quotes/${id}/status`, { status })).data 
};
export const testimonialsAPI = { getAll: async()=> (await apiClient.get('/testimonials')).data, create: async(t)=> (await apiClient.post('/testimonials', t)).data };
export const companyAPI = { getInfo: async()=> (await apiClient.get('/company-info')).data };
export const healthCheck = async()=> (await apiClient.get('/health')).data;

// Admin products
export const adminProductsAPI = {
  getAll: async()=> (await adminApiClient.get('/products')).data,
  create: async(p)=> (await adminApiClient.post('/products', p)).data,
  update: async(id,p)=> (await adminApiClient.put(`/products/${id}`, p)).data,
  remove: async(id)=> (await adminApiClient.delete(`/products/${id}`)).data,
  hardDelete: async(id)=> (await adminApiClient.delete(`/products/${id}/hard`)).data,
  updatePrice: async(id, price)=> (await adminApiClient.patch(`/products/${id}/price`, null, { params: { price } })).data,
  updateAvailability: async(id, available)=> (await adminApiClient.patch(`/products/${id}/availability`, null, { params: { available } })).data,
  bulkReplace: async(products, hideOld=true)=> (await adminApiClient.post(`/products/bulk_replace`, products, { params: { hide_old: hideOld } })).data,
  upload: async(file)=> (await adminApiClient.post(`/uploads`, (d=>{const f=new FormData(); f.append('file', file); return f;})())).data,
  listUploads: async(prefix)=> (await adminApiClient.get(`/uploads`, { params: { prefix } })).data,
  deleteUpload: async(path)=> (await adminApiClient.delete(`/uploads`, { params: { path } })).data,
};

// Admin comments
export const adminCommentsAPI = { list: async()=> (await adminApiClient.get('/comments')).data, approve: async(id)=> (await adminApiClient.patch(`/comments/${id}/approve`)).data, remove: async(id)=> (await adminApiClient.delete(`/comments/${id}`)).data };

// Settings (site / hero)
export const settingsAPI = {
  getPublic: async()=> (await apiClient.get('/settings')).data,
  adminGet: async()=> (await adminApiClient.get('/settings')).data,
  adminUpdate: async(payload)=> (await adminApiClient.put('/settings', payload)).data,
  adminPublish: async()=> (await adminApiClient.post('/settings/publish')).data,
  transformImage: async({ source_url, fmt, quality=85, width })=> (await adminApiClient.post('/settings/transform_image', null, { params: { source_url, fmt, quality, width } })).data,
};

// Admin quotes
export const adminQuotesAPI = {
  list: async()=> (await adminApiClient.get('/quotes')).data,
  updateStatus: async(id, status)=> (await adminApiClient.patch(`/quotes/${id}/status`, null, { params: { status } })).data,
  delete: async(id)=> (await adminApiClient.delete(`/quotes/${id}`)).data
};

// Admin invoices
export const adminInvoicesAPI = {
  list: async()=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get('/invoices/admin/invoices', { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  getById: async(id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get(`/invoices/admin/invoices/${id}`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  create: async(data)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post('/invoices/admin/invoices', data, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  createFromQuote: async(quoteId, amount, tvaRate=20.0)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post(`/invoices/admin/invoices/from-quote/${quoteId}`, null, { params: { amount, tva_rate: tvaRate }, headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  update: async(id, updates)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.patch(`/invoices/admin/invoices/${id}`, updates, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  delete: async(id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.delete(`/invoices/admin/invoices/${id}`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  markPaid: async(id, paymentMethod='paypal', paymentReference=null)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post(`/invoices/admin/invoices/${id}/mark-paid`, null, { params: { payment_method: paymentMethod, payment_reference: paymentReference }, headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  downloadPDF: (id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    // Créer un lien temporaire pour télécharger le PDF
    const url = `${backendUrl}/api/invoices/admin/invoices/${id}/pdf`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    // Ajouter l'authentification dans les headers via fetch
    fetch(url, {
      headers: { 'Authorization': `Basic ${token}` }
    }).then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      });
  },
  getPayments: async(id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get(`/invoices/admin/invoices/${id}/payments`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  addPayment: async(id, paymentData)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post(`/invoices/admin/invoices/${id}/payments`, paymentData, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
};

// API pour les devis (quotes)
export const adminQuotesNewAPI = {
  list: async()=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get('/quotes_api/admin/quotes', { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  getById: async(id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get(`/quotes_api/admin/quotes/${id}`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  create: async(data)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post('/quotes_api/admin/quotes', data, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  update: async(id, updates)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.patch(`/quotes_api/admin/quotes/${id}`, updates, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  delete: async(id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.delete(`/quotes_api/admin/quotes/${id}`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  downloadPDF: (id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const url = `${backendUrl}/api/quotes_api/admin/quotes/${id}/pdf`;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    fetch(url, {
      headers: { 'Authorization': `Basic ${token}` }
    }).then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      });
  },
  // Acomptes
  addPayment: async(id, paymentData)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post(`/quotes_api/admin/quotes/${id}/payments`, paymentData, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  getPayments: async(id)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get(`/quotes_api/admin/quotes/${id}/payments`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
};

// API pour les paiements PayPal
export const paymentAPI = {
  // Générer un lien PayPal pour un devis
  generateQuotePaymentLink: async(quoteId, amount, currency='EUR', description='')=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post('/payments/admin/generate-payment-link', {
      quote_id: quoteId,
      amount: amount,
      currency: currency,
      description: description
    }, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
  // Générer un lien PayPal pour une facture
  generateInvoicePaymentLink: async(invoiceId, amount, currency='EUR', description='')=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.post('/payments/admin/generate-invoice-payment-link', null, {
      params: { 
        invoice_id: invoiceId,
        amount: amount,
        currency: currency,
        description: description
      },
      headers: { 'Authorization': `Basic ${token}` }
    })).data;
  },
  // Obtenir le statut de paiement d'un devis
  getQuotePaymentStatus: async(quoteId)=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get(`/payments/admin/quote/${quoteId}/payment-status`, { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
};

// API pour les statistiques
export const statsAPI = {
  trackVisit: async(data)=> (await apiClient.post('/stats/track', data)).data,
  getStats: async()=> {
    const token = (typeof window !== 'undefined') ? localStorage.getItem('adminAuth') : null;
    return (await apiClient.get('/stats/admin/stats', { headers: { 'Authorization': `Basic ${token}` } })).data;
  },
};

export default apiClient;